import { updateReceiverState, getReceiverState } from '../state/ReceiverStore'
import { sendToSender, broadcastToSenders } from './CastReceiverManager'
import { MessageType } from '../../constants/namespaces'
import { detectTouchDevice } from '../device/DeviceDetection'

/**
 * Maps fields from sender recipe format to receiver format
 * This handles mismatches between the sender and receiver types
 */
function mapRecipeFields(recipe: any): any {
  if (!recipe) return recipe;
  
  // Make a copy of the recipe to avoid modifying the original
  const mappedRecipe = { ...recipe };
  
  // Map steps properties
  if (mappedRecipe.steps && Array.isArray(mappedRecipe.steps)) {
    mappedRecipe.steps = mappedRecipe.steps.map((step: { id: string; instructions?: string; description?: string }) => {
      const mappedStep = { ...step };
      
      // Map 'description' to 'instructions' if needed
      if (step.description && !step.instructions) {
        mappedStep.instructions = step.description;
      } else if (!step.instructions && !step.description) {
        // Set a default value so the app doesn't break
        mappedStep.instructions = 'No instructions provided.';
      }
      
      return mappedStep;
    });
  }
  
  return mappedRecipe;
}

/**
 * Handles incoming messages from senders
 */
export function handleSenderMessage(event: any) {
  const senderId = event.senderId;
  const rawData = event.data;

  // Try to parse the message if it's a string
  let message: any;
  try {
    if (typeof rawData === 'string') {
      message = JSON.parse(rawData);
    } else {
      message = rawData;
    }
    
    const messageType = message.type;
    
    switch (messageType) {
      case MessageType.LOAD_RECIPE:
        handleLoadRecipe(senderId, message);
        break;
        
      case MessageType.NEXT_STEP:
        handleNextStep(senderId);
        break;
        
      case MessageType.PREVIOUS_STEP:
        handlePreviousStep(senderId);
        break;
        
      case MessageType.GO_TO_STEP:
        handleGoToStep(senderId, message);
        break;
        
      case MessageType.TOGGLE_INGREDIENTS:
        handleToggleIngredients(senderId, message);
        break;
        
      case MessageType.CHANGE_SERVINGS:
        handleChangeServings(senderId, message);
        break;
        
      case MessageType.START_TIMER:
        handleStartTimer(senderId, message);
        break;
        
      case MessageType.PAUSE_TIMER:
        handlePauseTimer(senderId);
        break;
        
      case MessageType.RESUME_TIMER:
        handleResumeTimer(senderId);
        break;
        
      case MessageType.CANCEL_TIMER:
        handleCancelTimer(senderId);
        break;
        
      case MessageType.REQUEST_DEVICE_INFO:
        handleDeviceInfoRequest(senderId);
        break;
        
      case MessageType.PING:
        handlePing(senderId);
        break;

      case 'ECHO':
        sendToSender(senderId, {
          type: 'ECHO_RESPONSE',
          data: {
            message: message.message || 'Echo received'
          },
          timestamp: Date.now()
        });
        break;
        
      case MessageType.LOAD_CONTENT:
        // Not implemented
        break;
        
      case MessageType.CUSTOM_COMMAND:
        handleCustomCommand(senderId, message);
        break;
        
      default:
        // Special case for string-based message types
        if (typeof messageType === 'string' && messageType.toUpperCase() === 'PING') {
          handlePing(senderId);
        } else if (typeof messageType === 'string' && messageType.toUpperCase() === 'ECHO') {
          sendToSender(senderId, {
            type: 'ECHO_RESPONSE',
            data: {
              message: message.message || 'Echo received'
            },
            timestamp: Date.now()
          });
        } else if (typeof messageType === 'string' && 
                   (messageType.toUpperCase() === 'LOAD_RECIPE' ||
                    messageType.includes('RECIPE'))) {
          handleLoadRecipe(senderId, message);
        } else if (typeof messageType === 'string' && 
                  (messageType.toUpperCase() === 'GO_TO_STEP' || 
                   messageType.toUpperCase() === 'GOTO_STEP' || 
                   messageType.includes('STEP'))) {
          // Handle the GO_TO_STEP command in a case-insensitive way
          handleGoToStep(senderId, message);
        } else if (typeof messageType === 'string' && 
                  (messageType.toUpperCase() === 'NEXT_STEP' || 
                   messageType.toUpperCase() === 'NEXT')) {
          // Handle NEXT_STEP in a case-insensitive way
          handleNextStep(senderId);
        } else if (typeof messageType === 'string' && 
                  (messageType.toUpperCase() === 'PREVIOUS_STEP' || 
                   messageType.toUpperCase() === 'PREV_STEP' || 
                   messageType.toUpperCase() === 'PREVIOUS')) {
          // Handle PREVIOUS_STEP in a case-insensitive way
          handlePreviousStep(senderId);
        } else if (typeof messageType === 'string' && 
                  (messageType.toUpperCase() === 'TOGGLE_INGREDIENTS' || 
                   messageType.toUpperCase() === 'TOGGLE_INGREDIENT' || 
                   messageType.toUpperCase().includes('INGREDIENT'))) {
          // Handle TOGGLE_INGREDIENTS in a case-insensitive way
          handleToggleIngredients(senderId, message);
        } else if (typeof messageType === 'string' && 
                  (messageType.toUpperCase() === 'START_TIMER' || 
                   messageType.toUpperCase() === 'START')) {
          // Handle START_TIMER in a case-insensitive way
          handleStartTimer(senderId, message);
        } else if (typeof messageType === 'string' && 
                  (messageType.toUpperCase() === 'PAUSE_TIMER' || 
                   messageType.toUpperCase() === 'PAUSE')) {
          // Handle PAUSE_TIMER in a case-insensitive way
          handlePauseTimer(senderId);
        } else if (typeof messageType === 'string' && 
                  (messageType.toUpperCase() === 'RESUME_TIMER' || 
                   messageType.toUpperCase() === 'RESUME')) {
          // Handle RESUME_TIMER in a case-insensitive way
          handleResumeTimer(senderId);
        } else if (typeof messageType === 'string' && 
                  (messageType.toUpperCase() === 'CANCEL_TIMER' || 
                   messageType.toUpperCase() === 'CANCEL')) {
          // Handle CANCEL_TIMER in a case-insensitive way
          handleCancelTimer(senderId);
        } else if (typeof messageType === 'string' && 
                  (messageType.toUpperCase() === 'CHANGE_SERVINGS' || 
                   messageType.toUpperCase().includes('SERVING'))) {
          // Handle CHANGE_SERVINGS in a case-insensitive way
          handleChangeServings(senderId, message);
        } else {
          sendErrorToSender(senderId, `Unknown command: ${messageType}`);
        }
    }
  } catch (error) {
    sendErrorToSender(senderId, `Failed to process message: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/**
 * Handles a load recipe request
 */
function handleLoadRecipe(senderId: string, message: any) {
  // Extract recipe from message - handle different formats
  let recipe;
  
  if (message.recipe) {
    recipe = message.recipe;
  } else if (message.data && message.data.recipe) {
    recipe = message.data.recipe;
  } else if (typeof message.data === 'string') {
    try {
      const parsedData = JSON.parse(message.data);
      recipe = parsedData.recipe || parsedData;
    } catch (e) {
      // Silent error
    }
  }
  
  // Extract initialStep from message - handle different formats
  const initialStep = message.initialStep || 
                    (message.data && message.data.initialStep) || 
                    0;
  
  // Validate recipe
  if (!recipe) {
    sendErrorToSender(senderId, 'No recipe provided');
    return;
  }
  
  // Ensure recipe has required fields
  if (!recipe.id) {
    recipe.id = 'recipe-' + Date.now(); // Generate an ID if none exists
  }
  
  if (!recipe.title) {
    recipe.title = 'Untitled Recipe';
  }
  
  if (!recipe.steps || !Array.isArray(recipe.steps) || recipe.steps.length === 0) {
    // Create a default step if none exists
    recipe.steps = [{
      id: 'step-1',
      instructions: 'No instructions provided.'
    }];
  }
  
  // Ensure each step has an ID
  recipe.steps = recipe.steps.map((step: any, index: number) => {
    if (!step.id) {
      step.id = `step-${index + 1}`;
    }
    return step;
  });
  
  // Make sure ingredients array exists
  if (!recipe.ingredients || !Array.isArray(recipe.ingredients)) {
    recipe.ingredients = [];
  }
  
  // Perform field name mapping if needed
  recipe = mapRecipeFields(recipe);
  
  // Update receiver state with the recipe
  updateReceiverState({
    recipe,
    currentStepIndex: initialStep,
    showingIngredients: false,
    timerActive: false,
    servings: recipe.servings || 4,
    lastUpdated: new Date().toISOString()
  });
  
  // Show visual indicator that recipe was loaded
  try {
    const statusDiv = document.getElementById('connection-status');
    if (statusDiv) {
      statusDiv.textContent = `Recipe loaded: ${recipe.title}`;
      statusDiv.style.backgroundColor = 'rgba(0,128,0,0.7)';
    }
  } catch (e) {
    // Silently ignore status display errors
  }
  
  // Send successful content load message
  sendToSender(senderId, {
    type: MessageType.CONTENT_LOADED,
    data: {
      contentId: recipe.id,
      currentStepIndex: initialStep,
      servings: recipe.servings || 4,
      recipeLoaded: true  // Explicitly indicate recipe is loaded
    },
    timestamp: Date.now()
  });
  
  // Also send a specific state update message to confirm recipe is loaded
  sendToSender(senderId, {
    type: MessageType.RECEIVER_STATE_UPDATE,
    data: {
      currentStepIndex: initialStep,
      recipeLoaded: true,  // Add explicit flag for recipe loaded status
      recipeName: recipe.title
    },
    timestamp: Date.now()
  });
  
  // Send state update to all senders
  broadcastReceiverState();
}

/**
 * Handles a next step request
 */
function handleNextStep(senderId: string) {
  const state = updateReceiverState((currentState) => {
    const recipe = currentState.recipe;
    if (!recipe || !recipe.steps) {
      return currentState;
    }
    
    const totalSteps = recipe.steps.length;
    const currentIndex = currentState.currentStepIndex || 0;
    const newIndex = Math.min(currentIndex + 1, totalSteps - 1);
    
    return {
      ...currentState,
      currentStepIndex: newIndex,
      lastUpdated: new Date().toISOString()
    };
  });
  
  // Acknowledge and broadcast state
  acknowledgeNavigationRequest(senderId, state.currentStepIndex);
  broadcastReceiverState();
}

/**
 * Handles a previous step request
 */
function handlePreviousStep(senderId: string) {
  const state = updateReceiverState((currentState) => {
    const currentIndex = currentState.currentStepIndex || 0;
    const newIndex = Math.max(currentIndex - 1, 0);
    
    return {
      ...currentState,
      currentStepIndex: newIndex,
      lastUpdated: new Date().toISOString()
    };
  });
  
  // Acknowledge and broadcast state
  acknowledgeNavigationRequest(senderId, state.currentStepIndex);
  broadcastReceiverState();
}

/**
 * Handles a go to specific step request
 */
function handleGoToStep(senderId: string, message: any) {
  // Extract stepId from different possible properties
  const stepId = message.stepId || 
                 message.step_id || 
                 message.id || 
                 (message.data && message.data.stepId) ||
                 (message.data && message.data.step_id) ||
                 (message.data && message.data.id);
  
  if (!stepId) {
    sendErrorToSender(senderId, 'No step ID provided for GO_TO_STEP command');
    return;
  }
  
  const state = updateReceiverState((currentState) => {
    const recipe = currentState.recipe;
    if (!recipe || !recipe.steps) {
      return currentState;
    }
    
    // Find step index by ID
    const stepIndex = recipe.steps.findIndex((step: { id: string }) => step.id === stepId);
    if (stepIndex === -1) {
      return currentState;
    }
    
    return {
      ...currentState,
      currentStepIndex: stepIndex,
      lastUpdated: new Date().toISOString()
    };
  });
  
  // Acknowledge and broadcast state
  acknowledgeNavigationRequest(senderId, state.currentStepIndex);
  broadcastReceiverState();
}

/**
 * Handles a toggle ingredients request
 */
function handleToggleIngredients(senderId: string, message: any) {
  // Extract visible from different possible properties
  const visible = typeof message.visible === 'boolean' ? message.visible :
                 typeof message.show === 'boolean' ? message.show :
                 typeof message.display === 'boolean' ? message.display :
                 typeof (message.data && message.data.visible) === 'boolean' ? message.data.visible :
                 typeof (message.data && message.data.show) === 'boolean' ? message.data.show :
                 typeof (message.data && message.data.display) === 'boolean' ? message.data.display :
                 undefined;
  
  const state = updateReceiverState((currentState) => {
    // If visible is undefined, toggle current state
    const showingIngredients = visible !== undefined 
      ? visible 
      : !currentState.showingIngredients;
    
    return {
      ...currentState,
      showingIngredients,
      lastUpdated: new Date().toISOString()
    };
  });
  
  // Acknowledge change
  sendToSender(senderId, {
    type: MessageType.RECEIVER_STATE_UPDATE,
    data: {
      showingIngredients: state.showingIngredients
    },
    timestamp: Date.now()
  });
  
  // Broadcast state to all senders
  broadcastReceiverState();
}

/**
 * Handles a change servings request
 */
function handleChangeServings(senderId: string, message: any) {
  // Extract servings from different possible properties
  const servings = message.servings || 
                  message.serving || 
                  message.count || 
                  (message.data && message.data.servings) ||
                  (message.data && message.data.serving) ||
                  (message.data && message.data.count);
  
  if (typeof servings !== 'number' || servings <= 0) {
    sendErrorToSender(senderId, 'Invalid servings value');
    return;
  }
  
  const state = updateReceiverState((currentState) => {
    return {
      ...currentState,
      servings,
      lastUpdated: new Date().toISOString()
    };
  });
  
  // Acknowledge change
  sendToSender(senderId, {
    type: MessageType.RECEIVER_STATE_UPDATE,
    data: {
      servings: state.servings
    },
    timestamp: Date.now()
  });
  
  // Broadcast state to all senders
  broadcastReceiverState();
}

/**
 * Handles a start timer request
 */
function handleStartTimer(senderId: string, message: any) {
  // Extract duration from different possible properties
  const duration = message.duration || 
                   message.time || 
                   message.seconds || 
                   (message.data && message.data.duration) ||
                   (message.data && message.data.time) ||
                   (message.data && message.data.seconds);
  
  // Extract stepId from different possible properties
  const stepId = message.stepId || 
                 message.step_id || 
                 message.step || 
                 (message.data && message.data.stepId) ||
                 (message.data && message.data.step_id) ||
                 (message.data && message.data.step);
  
  if (typeof duration !== 'number' || duration <= 0) {
    sendErrorToSender(senderId, 'Invalid timer duration');
    return;
  }
  
  const state = updateReceiverState((currentState) => {
    return {
      ...currentState,
      timerActive: true,
      timerRemaining: duration,
      timerStepId: stepId,
      lastUpdated: new Date().toISOString()
    };
  });
  
  // Acknowledge timer start
  sendToSender(senderId, {
    type: MessageType.RECEIVER_STATE_UPDATE,
    data: {
      timerActive: state.timerActive,
      timerRemaining: state.timerRemaining
    },
    timestamp: Date.now()
  });
  
  // Broadcast state to all senders
  broadcastReceiverState();
}

/**
 * Handles a pause timer request
 */
function handlePauseTimer(senderId: string) {
  const state = updateReceiverState((currentState) => {
    if (!currentState.timerActive) {
      return currentState;
    }
    
    return {
      ...currentState,
      timerActive: false,
      lastUpdated: new Date().toISOString()
    };
  });
  
  // Acknowledge and broadcast state
  sendToSender(senderId, {
    type: MessageType.RECEIVER_STATE_UPDATE,
    data: {
      timerActive: state.timerActive,
      timerRemaining: state.timerRemaining
    },
    timestamp: Date.now()
  });
  
  // Broadcast state to all senders
  broadcastReceiverState();
}

/**
 * Handles a resume timer request
 */
function handleResumeTimer(senderId: string) {
  const state = updateReceiverState((currentState) => {
    if (currentState.timerActive || !currentState.timerRemaining) {
      return currentState;
    }
    
    return {
      ...currentState,
      timerActive: true,
      lastUpdated: new Date().toISOString()
    };
  });
  
  // Acknowledge timer resume
  sendToSender(senderId, {
    type: MessageType.RECEIVER_STATE_UPDATE,
    data: {
      timerActive: state.timerActive,
      timerRemaining: state.timerRemaining
    },
    timestamp: Date.now()
  });
  
  // Broadcast state to all senders
  broadcastReceiverState();
}

/**
 * Handles a cancel timer request
 */
function handleCancelTimer(senderId: string) {
  const state = updateReceiverState((currentState) => {
    return {
      ...currentState,
      timerActive: false,
      timerRemaining: undefined,
      timerStepId: undefined,
      lastUpdated: new Date().toISOString()
    };
  });
  
  // Acknowledge timer cancel
  sendToSender(senderId, {
    type: MessageType.RECEIVER_STATE_UPDATE,
    data: {
      timerActive: state.timerActive,
      timerRemaining: state.timerRemaining
    },
    timestamp: Date.now()
  });
  
  // Broadcast state to all senders
  broadcastReceiverState();
}

/**
 * Handles a device info request
 */
function handleDeviceInfoRequest(senderId: string) {
  const hasTouchScreen = detectTouchDevice();
  const { width, height } = getScreenDimensions();
  const userAgent = navigator.userAgent;
  
  // Determine display type based on dimensions and user agent
  const displayType = getDisplayType(width, height, userAgent);
  
  sendToSender(senderId, {
    type: MessageType.DEVICE_INFO,
    data: {
      hasTouchScreen,
      displayType,
      width,
      height,
      userAgent
    },
    timestamp: Date.now()
  });
}

/**
 * Handles a ping request
 */
function handlePing(senderId: string) {
  // Always respond with PONG
  sendToSender(senderId, {
    type: MessageType.PONG,
    data: {},
    timestamp: Date.now()
  });
  
  // Also send current state if recipe is loaded
  const state = getReceiverState();
  if (state.recipe) {
    sendToSender(senderId, {
      type: MessageType.RECEIVER_STATE_UPDATE,
      data: {
        recipeLoaded: true,
        recipeName: state.recipe.title,
        currentStepIndex: state.currentStepIndex
      },
      timestamp: Date.now()
    });
  } else {
    sendToSender(senderId, {
      type: MessageType.RECEIVER_STATE_UPDATE,
      data: {
        recipeLoaded: false
      },
      timestamp: Date.now()
    });
  }
}

/**
 * Handles a custom command
 */
function handleCustomCommand(senderId: string, message: any) {
  const data = message.data || message;
  const command = data.command || (message.command || '');
  const params = data.params || {};
  
  // Special handling for recipe wrapped in custom command
  // This is used by some senders as a fallback approach
  if (message.recipe || data.recipe) {
    const recipeMessage = {
      ...message,
      type: MessageType.LOAD_RECIPE,
      recipe: message.recipe || data.recipe,
      initialStep: message.initialStep || data.initialStep || 0
    };
    
    // Forward to the recipe handler
    handleLoadRecipe(senderId, recipeMessage);
    return;
  }
  
  // Also check if the command itself is 'LOAD_RECIPE'
  if (command === 'LOAD_RECIPE' || command === MessageType.LOAD_RECIPE || 
      command === 'LOAD_RECIPE' || String(command).toUpperCase() === 'LOAD_RECIPE') {
    
    const recipeMessage = {
      ...message,
      type: MessageType.LOAD_RECIPE,
      recipe: data.recipe || params.recipe || (message.params && message.params.recipe),
      initialStep: data.initialStep || params.initialStep || 
                 (message.params && message.params.initialStep) || 0
    };
    
    // Forward to the recipe handler
    handleLoadRecipe(senderId, recipeMessage);
    return;
  }
  
  if (!command) {
    sendErrorToSender(senderId, 'No command specified');
    return;
  }
  
  // Handle custom commands here based on your app's needs
  switch (command) {
    case 'LOAD_TEST_RECIPE':
      // Create a test recipe
      const testRecipe = {
        id: 'test-recipe-' + Date.now(),
        title: 'Test Recipe (From Sender)',
        servings: 2,
        prepTime: 5,
        cookTime: 10,
        totalTime: 15,
        ingredients: [
          'First test ingredient',
          'Second test ingredient',
          'Third test ingredient with a longer description to test wrapping',
          'Fourth test ingredient'
        ],
        steps: [
          { 
            id: 'step-1', 
            instructions: 'This is the first step of the test recipe. This should appear clearly on the screen.'
          },
          { 
            id: 'step-2', 
            instructions: 'This is the second step with a timer.', 
            timer: 30
          },
          { 
            id: 'step-3', 
            instructions: 'This is the third and final step of the test recipe.'
          }
        ],
        hasTouchControls: true
      };
      
      // Load the test recipe
      updateReceiverState({
        recipe: testRecipe,
        currentStepIndex: 0,
        showingIngredients: false,
        timerActive: false,
        servings: testRecipe.servings,
        lastUpdated: new Date().toISOString()
      });
      
      // Send confirmation to sender
      sendToSender(senderId, {
        type: MessageType.CONTENT_LOADED,
        data: {
          contentId: testRecipe.id,
          message: 'Test recipe loaded successfully',
          currentStepIndex: 0,
          servings: testRecipe.servings,
          recipeLoaded: true
        },
        timestamp: Date.now()
      });
      
      // Also broadcast the state to all senders
      broadcastReceiverState();
      
      // Send multiple confirmation messages for better visibility
      setTimeout(() => {
        sendToSender(senderId, {
          type: 'RECEIVER_CONFIRM',
          data: {
            message: 'Test recipe loaded and displaying'
          },
          timestamp: Date.now()
        });
        broadcastReceiverState(); // Send another state update
      }, 1000);
      
      // Two seconds later, send one more confirmation
      setTimeout(() => {
        sendToSender(senderId, {
          type: 'ECHO_RESPONSE',
          data: {
            message: 'Recipe is now visible on screen'
          },
          timestamp: Date.now()
        });
      }, 2000);
      
      break;
      
    case 'TEST_CONNECTION':
      sendToSender(senderId, {
        type: MessageType.PONG,
        data: { message: 'Connection successful' },
        timestamp: Date.now()
      });
      break;
      
    case 'getDiagnostics':
      // Example command
      sendToSender(senderId, {
        type: MessageType.CUSTOM_COMMAND,
        data: {
          command: 'diagnosticsResult',
          result: {
            receiverVersion: '1.0.0',
            connectionQuality: 'good',
            // Safely check if performance.memory exists (it's non-standard)
            memoryUsage: typeof performance !== 'undefined' && 
                         'memory' in performance && 
                         performance.memory ? 
                         (performance.memory as any).usedJSHeapSize : 
                         'unknown'
          }
        },
        timestamp: Date.now()
      });
      break;
      
    default:
      sendErrorToSender(senderId, `Unknown command: ${command}`);
  }
}

/**
 * Sends an error message to a sender
 */
function sendErrorToSender(senderId: string, errorMessage: string, code: string = 'ERROR') {
  sendToSender(senderId, {
    type: MessageType.ERROR,
    data: {
      code: code,
      message: errorMessage,
      details: undefined
    },
    timestamp: Date.now()
  });
}

/**
 * Acknowledges a navigation request
 */
function acknowledgeNavigationRequest(senderId: string, currentStepIndex: number) {
  sendToSender(senderId, {
    type: MessageType.RECEIVER_STATE_UPDATE,
    data: {
      currentStepIndex
    },
    timestamp: Date.now()
  });
}

/**
 * Broadcasts the current receiver state to all senders
 */
function broadcastReceiverState() {
  const state = updateReceiverState((currentState) => currentState);
  const { recipe, currentStepIndex, showingIngredients, timerActive, timerRemaining, servings } = state;
  
  if (!recipe) {
    // Send an explicit 'no recipe' state to help clients
    broadcastToSenders({
      type: MessageType.RECEIVER_STATE_UPDATE,
      data: {
        recipeLoaded: false
      },
      timestamp: Date.now()
    });
    return;
  }
  
  const currentStepId = recipe.steps && recipe.steps[currentStepIndex] 
    ? recipe.steps[currentStepIndex].id 
    : null;
  
  broadcastToSenders({
    type: MessageType.RECEIVER_STATE_UPDATE,
    data: {
      currentStepId,
      currentStepIndex,
      showingIngredients,
      timerActive,
      timerRemaining,
      servings,
      recipeLoaded: true,  // Add explicit flag for recipe loaded status
      recipeName: recipe.title
    },
    timestamp: Date.now()
  });
}

/**
 * Gets the screen dimensions
 */
function getScreenDimensions() {
  return {
    width: window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
    height: window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
  };
}

/**
 * Determines the display type based on dimensions and user agent
 */
function getDisplayType(width: number, height: number, userAgent: string): 'TV' | 'MONITOR' | 'TABLET' | 'PHONE' | 'UNKNOWN' {
  // Check for Chromecast
  if (/CrKey/.test(userAgent)) {
    return 'TV';
  }
  
  // Check for mobile devices
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    return width < 768 ? 'PHONE' : 'TABLET';
  }
  
  // Check for TV-like dimensions
  if (width >= 1920 && Math.abs(width / height - 16 / 9) < 0.1) {
    return 'TV';
  }
  
  // Default to monitor for larger screens
  return width >= 768 ? 'MONITOR' : 'UNKNOWN';
}
